import React, { useRef, useState } from 'react';
import { OutlineButton } from '../../components/display/button';
import { Tooltip, TooltipRefProps } from 'react-tooltip';
import { trpc } from '../../utils/trpc-client';
import { LineItemsRowV2 } from 'shared/src/domain/io/io-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { IODropdown } from '../../features/strategy/io-dropdown';
import { useOnClickOutside } from 'usehooks-ts';
import {
  CopyType,
  estimatedUnitsMgmtFeeFormula,
  estimatedUnitsStandardFormula,
  getCopyTypeLabel,
  isMgmtFeeItemV2,
  isZeroDollarItemV2,
  mediaBudgetZeroDollarFormula,
  removeLineBreaks
} from '../../features/strategy/pricing-grid-utils';
import { useProposalContext } from '@/features/proposals/proposal-context';
import { CopyToClipboard } from '@/icons/copy-to-clipboard';
import { CopiedToClipboard } from '@/icons/copied-to-clipboard';
import { FileExclamation } from '@/icons/file-exclamation';

export function CopyPricingGridV2() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { proposal } = useProposalContext();
  const { data: lineItems } = trpc.ioExportV2.useQuery({ proposal_id: proposal.id });
  const tooltipRef = useRef<TooltipRefProps>(null);
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const [copyType, setCopyType] = useState<CopyType | null>(null);
  useOnClickOutside(containerRef as React.RefObject<HTMLElement>, () => setTooltipOpen(false));

  if (lineItems === undefined) {
    return (
      <div ref={containerRef}>
        <OutlineButton data-tooltip-id="copy-pricing-grid" disabled onClick={() => null}>
          <div className="flex items-center gap-2">
            <CopyToClipboard color="gray" />
            <div>Pricing Grid</div>
          </div>
        </OutlineButton>
        <Tooltip
          id="copy-pricing-grid"
          place="bottom-start"
          style={{
            backgroundColor: '#111928',
            borderRadius: '5px',
            color: 'white',
            zIndex: '9999'
          }}>
          Publish line items to copy pricing grid
        </Tooltip>
      </div>
    );
  }

  if (!copyType) {
    return (
      <div ref={containerRef}>
        <OutlineButton
          data-tooltip-id="copy-pricing-grid"
          selected={tooltipOpen}
          onClick={() => setTooltipOpen(!tooltipOpen)}>
          <CopyToClipboard color={'blue'} />
          <div>Pricing Grid</div>
        </OutlineButton>
        <IODropdown
          id="copy-pricing-grid"
          afterHide={() => setCopyType(null)}
          isOpen={tooltipOpen}
          tooltipRef={tooltipRef}
          setIsOpen={setTooltipOpen}>
          <div className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white px-4 pb-4 pt-2">
            <div className="font-semibold text-gray-800">Choose a Pricing Grid template</div>
            <div className="flex flex-col gap-1">
              <div
                className="max-w-fit cursor-pointer rounded-sm p-1 text-sm text-gray-600 hover:bg-blue-100 hover:text-gray-800"
                onClick={async () => {
                  await navigator.clipboard.writeText(formatStandardTemplate(lineItems));
                  setCopyType('standard');
                }}>
                Standard Template
              </div>
              <div
                className="max-w-fit cursor-pointer rounded-sm p-1 text-sm text-gray-600 hover:bg-blue-100 hover:text-gray-800"
                onClick={async () => {
                  await navigator.clipboard.writeText(formatNetGrossTemplate(lineItems));
                  setCopyType('net/gross');
                }}>
                Agency Net/Gross Template
              </div>
              <div
                className="max-w-fit cursor-pointer rounded-sm p-1 text-sm text-gray-600 hover:bg-blue-100 hover:text-gray-800"
                onClick={async () => {
                  await navigator.clipboard.writeText(formatManagementFeeTemplate(lineItems));
                  setCopyType('mgmt-fee');
                }}>
                Management Fee Template
              </div>
            </div>
          </div>
        </IODropdown>
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <OutlineButton
        data-tooltip-id="copy-pricing-grid"
        selected={tooltipOpen}
        onClick={() => setTooltipOpen(!tooltipOpen)}
        position="left">
        <CopiedToClipboard />
        <div>Copied</div>
      </OutlineButton>
      <IODropdown
        id="copy-pricing-grid"
        afterHide={() => setCopyType(null)}
        isOpen={tooltipOpen}
        tooltipRef={tooltipRef}
        setIsOpen={setTooltipOpen}>
        <div className="flex w-[300px] flex-col gap-4 rounded-lg bg-blue-50 px-4 py-2 text-gray-800">
          <div className="flex items-center justify-between font-semibold">
            What to do next...
            <div
              className="flex cursor-pointer rounded-sm bg-blue-100 px-[3px] py-px"
              onClick={() => tooltipRef.current?.close()}>
              <FontAwesomeIcon icon={faXmark} className="text-lg text-[#3b6fec]" />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-2">
              <div className="flex items-start text-lg font-bold leading-none text-blue-700">1</div>
              <div className="text-sm">
                Go to the {getCopyTypeLabel(copyType)} Pricing Grid tab of your Google Sheets
                Strategy Workbook.
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="flex items-start text-lg font-bold leading-none text-blue-700">2</div>
              <div className="text-sm">Paste into the first empty cell of the Grid.</div>
            </div>
          </div>
          <div className="flex rounded-sm bg-blue-200 text-blue-800">
            <div className="flex items-center px-1">
              <FileExclamation color={'blue'} />
            </div>
            <div className="rounded bg-blue-100 px-2 py-1 text-xs font-medium">
              If line items are adjusted in the Strategy Workbook, be sure to update those line
              items in Bravo!
            </div>
          </div>
        </div>
      </IODropdown>
    </div>
  );
}

function formatStandardTemplate(items: LineItemsRowV2[]) {
  const unitPriceField = 'INDIRECT("K" & ROW())';
  const priceField = 'INDIRECT("L" & ROW())';
  const unitPriceTypeField = 'INDIRECT("I" & ROW())';

  return items
    .map(item => {
      const isMgmtFee = isMgmtFeeItemV2(item);
      const isZeroDollar = isZeroDollarItemV2(item);
      return [
        item.start_date, // Start Date - B
        item.end_date, // End Date - C
        removeLineBreaks(item.tactic), // Tactics - D
        removeLineBreaks(item.geo), // Geo - E
        removeLineBreaks(item.audience), // Audience - F
        removeLineBreaks(item.targeting_details), // Targeting Details - G
        removeLineBreaks(item.ad_size), // Ad Formats - H
        removeLineBreaks(item.unit_price_type), // Unit Price Type - I
        isMgmtFee || isZeroDollar
          ? item.estimated_units
          : estimatedUnitsStandardFormula(unitPriceField, priceField, unitPriceTypeField), // Estimated Units - J
        isMgmtFee || isZeroDollar ? item.estimated_unit_cost : item.unit_price, // Unit Price - K
        item.price // Price - L
      ]
        .map(value => (typeof value === 'string' ? value.trim() : value))
        .join('\t');
    })
    .join('\n');
}

function formatNetGrossTemplate(items: LineItemsRowV2[]) {
  const unitPriceField = 'INDIRECT("L" & ROW())';
  const priceField = 'INDIRECT("N" & ROW())';
  const unitPriceTypeField = 'INDIRECT("I" & ROW())';
  const advertiserUnitPriceFormula = `=${unitPriceField}/(1-C2)`;
  const advertiserPriceFormula = `=${priceField}/(1-C2)`;

  return items
    .map(item => {
      const isMgmtFee = isMgmtFeeItemV2(item);
      const isZeroDollar = isZeroDollarItemV2(item);
      return [
        item.start_date, // Start Date - B
        item.end_date, // End Date - C
        removeLineBreaks(item.tactic), // Tactics - D
        removeLineBreaks(item.geo), // Geo - E
        removeLineBreaks(item.audience), // Audience - F
        removeLineBreaks(item.targeting_details), // Targeting Details - G
        removeLineBreaks(item.ad_size), // Ad Formats - H
        removeLineBreaks(item.unit_price_type), // Unit Price Type - I
        isMgmtFee || isZeroDollar
          ? item.estimated_units
          : estimatedUnitsStandardFormula(unitPriceField, priceField, unitPriceTypeField), // Estimated Units - J
        advertiserUnitPriceFormula, // Advertiser Unit Price - K
        isMgmtFee || isZeroDollar ? item.estimated_unit_cost : item.unit_price, // Unit Price - L
        advertiserPriceFormula, // Advertiser Price - M
        item.price // Price - N
      ]
        .map(value => (typeof value === 'string' ? value.trim() : value))
        .join('\t');
    })
    .join('\n');
}

function formatManagementFeeTemplate(items: LineItemsRowV2[]) {
  const unitPriceTypeField = 'INDIRECT("I" & ROW())';
  const estimatedUnitsField = 'INDIRECT("J" & ROW())';
  const unitPriceField = 'INDIRECT("K" & ROW())';
  const mgmtFeeField = 'INDIRECT("L" & ROW())';
  const mediaBudgetField = 'INDIRECT("M" & ROW())';
  const priceField = 'INDIRECT("O" & ROW())';

  function estimatedUnits(item: LineItemsRowV2) {
    if (isMgmtFeeItemV2(item))
      return estimatedUnitsMgmtFeeFormula(mediaBudgetField, unitPriceField, unitPriceTypeField);
    if (isZeroDollarItemV2(item)) return item.estimated_units;
    return estimatedUnitsStandardFormula(unitPriceField, priceField, unitPriceTypeField);
  }

  function mediaBudget(item: LineItemsRowV2) {
    if (isZeroDollarItemV2(item))
      return mediaBudgetZeroDollarFormula(estimatedUnitsField, unitPriceField, unitPriceTypeField);
    if (isMgmtFeeItemV2(item)) return item.estimated_media_cost;
    return 'N/A';
  }

  return items
    .map(item => {
      const isMgmtFee = isMgmtFeeItemV2(item);
      const isZeroDollar = isZeroDollarItemV2(item);
      return [
        item.start_date, // Start Date - B
        item.end_date, // End Date - C
        removeLineBreaks(item.tactic), // Tactics - D
        removeLineBreaks(item.geo), // Geo - E
        removeLineBreaks(item.audience), // Audience - F
        removeLineBreaks(item.targeting_details), // Targeting Details - G
        removeLineBreaks(item.ad_size), // Ad Formats - H
        removeLineBreaks(item.unit_price_type), // Unit Price Type - I
        estimatedUnits(item), // Estimated Units - J
        isMgmtFee || isZeroDollar ? item.estimated_unit_cost : item.unit_price, // Unit Price - K
        isMgmtFee ? item.price : 'N/A', // MGMT Fee - L
        mediaBudget(item), // Media Budget - M
        isMgmtFee ? `=${mgmtFeeField}+${mediaBudgetField}` : 'N/A', // Total Line Item Advertiser Budget - N
        item.price // Price - 0
      ]
        .map(value => (typeof value === 'string' ? value.trim() : value))
        .join('\t');
    })
    .join('\n');
}
